/* eslint-disable */
// src/tables/visitStrategies/BaseStrategy.js

import React from "react";
import { NumericFormat } from "react-number-format";
import { Tooltip } from "@mui/material";
import {
  isInsertedInCurrentVisitNoticeRow,
  isVisitMachineCollected,
} from "utils/visitCollectionState";
import {
  buildMachineDisplayName,
  getLocalDisplayNumber,
  getMachineBaseName,
} from "utils/machineDisplayName";
import AutoGrowingNoteTextarea from "components/visit/AutoGrowingNoteTextarea";
import {
  centeredDataBlockStyle,
  getIndividualEditorSizing,
} from "components/visit/individualEditorSizing";

/* Base con helpers de UI comunes a todas las estrategias.
 * nf y moneyFmt vienen por contexto (ctx.helpers).
 */
export default class BaseStrategy {
  constructor(ctx) {
    this.ctx = ctx;
  }

  // ==== getters cortos ====
  get mode() {
    return this.ctx.mode;
  }
  get isEdit() {
    return this.ctx.mode === "edit";
  }
  get isRead() {
    return this.ctx.mode === "read";
  }

  get nf() {
    return this.ctx.helpers.nf;
  } // puede ser nfDynamic
  get moneyFmt() {
    return this.ctx.helpers.moneyFmt;
  } // puede ser moneyFmtDynamic
  get getLastVisit() {
    return this.ctx.helpers.getLastVisit;
  }
  get computeKey() {
    return this.ctx.helpers.computeKey;
  }
  get userInputs() {
    return this.ctx.helpers.userInputs;
  }
  get settledInputFields() {
    return this.ctx.helpers.settledInputFields || {};
  }
  get handleInputChange() {
    return this.ctx.helpers.handleInputChange;
  }
  get handleToggleReset() {
    return this.ctx.helpers.handleToggleReset;
  }
  get wasCollectedByLM() {
    return this.ctx.helpers.wasCollectedByLM || {};
  }
  get handleToggleWasCollected() {
    return this.ctx.helpers.handleToggleWasCollected || (() => {});
  }
  get counterVerificationContextsByLocalMachineId() {
    return this.ctx.helpers.counterVerificationContextsByLocalMachineId || {};
  }
  get isCounterVerificationExpanded() {
    return this.ctx.helpers.isCounterVerificationExpanded || (() => false);
  }
  get toggleCounterVerificationExpanded() {
    return this.ctx.helpers.toggleCounterVerificationExpanded || (() => {});
  }
  get lastGlobalVisitId() {
    return this.ctx.helpers.lastGlobalVisitId;
  }
  get makeLabel() {
    return this.ctx.helpers.makeLabel;
  }
  get getFormulaName() {
    return this.ctx.helpers.getFormulaName;
  }
  get getFormulaValue() {
    return this.ctx.helpers.getFormulaValue;
  }

  get rowLine() {
    const isTragamonedas = String(this.ctx.type || "")
      .toLowerCase()
      .includes("tragamoneda");
    return isTragamonedas
      ? {
          ...this.ctx.ui.rowLine,
          justifyContent: "space-between",
          width: "100%",
        }
      : this.ctx.ui.rowLine;
  }
  get textInputStyle() {
    return this.ctx.ui.textInputStyle;
  }
  get numericProps() {
    return this.ctx.ui.numericProps;
  } // << imprescindible
  get compact() {
    return this.ctx.options.compact;
  }
  get individualEditorLayout() {
    return this.ctx.options?.individualEditorLayout === true;
  }
  get collectionDetailPresentation() {
    return this.ctx.options?.collectionDetailPresentation === true;
  }

  // bordoTotal: color único para Totales negativos (bordo / rojo apagado)
  NEG_COLOR = "#B31D28";
  negStyle(value) {
    const n = Number(value);
    return Number.isFinite(n) && n < 0 ? { color: this.NEG_COLOR } : {};
  }
  pickNumber(obj, ...keys) {
    for (const key of keys) {
      const value = obj?.[key];
      if (typeof value === "number" && Number.isFinite(value)) return value;
      if (value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value))) {
        return Number(value);
      }
    }
    return null;
  }
  toIdOrNull(value) {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  formatDateDDMM(dateLike) {
    if (!dateLike) return null;
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return null;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}`;
  }
  getPrevDateSuffix(prevVisitId, prevVisitDate) {
    const prevId = this.toIdOrNull(prevVisitId);
    const lastId = this.toIdOrNull(this.lastGlobalVisitId);
    // Regla UX: solo mostrar "(dd/MM)" si el Prev proviene de una visita distinta
    // a la última recaudación global relevante del local.
    if (!prevId || !lastId || prevId === lastId) return "";
    const ddmm = this.formatDateDDMM(prevVisitDate);
    return ddmm ? ` (${ddmm})` : "";
  }

  isWasCollected(m, keyId = null) {
    return isVisitMachineCollected(m, {
      wasCollectedByLM: this.wasCollectedByLM,
      keyId: keyId ?? this.computeKey(m),
    });
  }

  renderWasCollectedButton(m, keyId, styleOverride = {}) {
    if (isInsertedInCurrentVisitNoticeRow(m) || !this.isEdit) return null;
    const isCollected = this.isWasCollected(m, keyId);
    return (
      <button
        type="button"
        className={this.individualEditorLayout ? "individual-editor-action" : undefined}
        style={{
          border: "1px solid #b7bcc5",
          borderRadius: 6,
          background: "#fff",
          padding: "4px 8px",
          cursor: "pointer",
          fontSize: this.individualEditorLayout ? 16 : 12,
          ...styleOverride,
        }}
        onClick={() => this.handleToggleWasCollected(keyId)}
      >
        {isCollected
          ? this.individualEditorLayout
            ? "No recaudar"
            : "NO RECAUDAR"
          : this.individualEditorLayout
            ? "Recaudar"
            : "RECAUDAR"}
      </button>
    );
  }

  renderWasCollectedToggle(m, keyId) {
    if (isInsertedInCurrentVisitNoticeRow(m)) return null;
    if (!this.isEdit) return null;
    const isCollected = this.isWasCollected(m, keyId);
    return (
      <div
        style={{
          marginTop: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: this.individualEditorLayout ? "center" : undefined,
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        {this.renderWasCollectedButton(m, keyId)}
        {!isCollected && (
          <span
            style={{
              fontSize: this.individualEditorLayout ? 16 : 12,
              fontStyle: "italic",
              opacity: 0.85,
            }}
          >
            máquina no recaudada
          </span>
        )}
      </div>
    );
  }

  // ---------- UI helpers compartidos ----------
  MachineCell({
    m,
    hasRent,
    formulaText,
    machineActions = null,
    plainStatusText = null,
    showWasCollectedToggle = true,
  }) {
    const machineType = m?.MachineType?.Type ?? m?.MachineType ?? m?.MachineTypeType ?? null;
    const useCollectionDetailUncollectedNotice =
      this.collectionDetailPresentation && machineType === "Tragamoneda" && !hasRent;
    const useCollectionDetailUnreadNotice = this.collectionDetailPresentation && !useCollectionDetailUncollectedNotice;
    const localDisplayNumber = getLocalDisplayNumber(m);
    const detailDisplayName = `${
      machineType === "Tragamoneda" && !hasRent && localDisplayNumber
        ? `${localDisplayNumber} - `
        : ""
    }${getMachineBaseName(m, "Máquina desconocida")}`;
    const name = this.collectionDetailPresentation
      ? detailDisplayName
      : this.individualEditorLayout
        ? `${
            machineType === "Tragamoneda" && !hasRent && localDisplayNumber
              ? `${localDisplayNumber} - `
              : ""
          }${getMachineBaseName(m, "Máquina desconocida")}`
        : buildMachineDisplayName(m, { fallback: "Máquina desconocida" });
    const keyId = this.computeKey(m);
    const isCollected = this.isWasCollected(m, keyId);

    const nameFontSize = this.individualEditorLayout ? 20 : this.compact ? 15 : 16; // px
    const centeredHeaderRow = this.individualEditorLayout
      ? { ...this.rowLine, justifyContent: "center", textAlign: "center", flexWrap: "wrap" }
      : this.rowLine;

    return (
      <div
        style={
          this.individualEditorLayout
            ? { display: "grid", justifyItems: "center", gap: 4, textAlign: "center" }
            : undefined
        }
      >
        {/* 1) Nombre */}
        <div style={centeredHeaderRow}>
          <span
            style={{
              fontWeight: 700,
              fontSize: nameFontSize,
              lineHeight: 1.15,
            }}
          >
            {name}
          </span>
          {machineActions}
        </div>

        {/* 2) Badge Alquilada */}
        {hasRent && (
          <div style={centeredHeaderRow}>
            <span style={{ color: "inherit" }}>Alquilada</span>
          </div>
        )}

        {/* 3) localMachineId (solo en el render tabular anterior) */}
        {!this.individualEditorLayout && !this.collectionDetailPresentation && (
          <div style={centeredHeaderRow}>
            <span>localMachineId: {m.LocalMachineId ?? "-"}</span>
          </div>
        )}

        {/* 4) Formula / texto secundario (cuando corresponda) */}
        {formulaText && (
          <div
            style={{
              ...centeredHeaderRow,
              fontSize: this.individualEditorLayout ? 16 : 12,
              fontStyle: this.collectionDetailPresentation && !hasRent ? "normal" : "italic",
              opacity: 1,
              color: "#000000",
            }}
          >
            {formulaText}
          </div>
        )}

        {plainStatusText && (
          <div
            style={{
              ...centeredHeaderRow,
              fontSize: this.individualEditorLayout ? 16 : 12,
              fontStyle: "normal",
              opacity: 0.85,
            }}
          >
            {plainStatusText}
          </div>
        )}

        {!isCollected && this.isRead && !isInsertedInCurrentVisitNoticeRow(m) && (
          <div
            style={{
              ...centeredHeaderRow,
              fontSize: this.individualEditorLayout ? 16 : 12,
              fontStyle: this.collectionDetailPresentation ? "normal" : "italic",
              opacity: 0.85,
            }}
          >
            {useCollectionDetailUncollectedNotice
              ? "No recaudada"
              : useCollectionDetailUnreadNotice
                ? "Sin lectura"
                : "dicha máquina no fue recaudada"}
          </div>
        )}
        {showWasCollectedToggle ? this.renderWasCollectedToggle(m, keyId) : null}

        {/* (sin nota aqui­: la nota va en su propia columna) */}
      </div>
    );
  }

  NoteCell({ keyId, m }) {
    // EDIT: en editar => input, en detalle (read) => texto plano
    if (this.isEdit) {
      const noteTextareaStyle = {
        ...this.textInputStyle,
        minHeight: 80,
        resize: "vertical",
        whiteSpace: "normal",
        overflowWrap: "break-word",
        lineHeight: 1.35,
        font: "inherit",
        boxSizing: "border-box",
      };
      const textareaProps = {
        id: `note-${keyId}`,
        name: `note-${keyId}`,
        "aria-label": this.makeLabel(
          m,
          this.individualEditorLayout ? "Nota de recaudación" : "Nota"
        ),
        placeholder: this.individualEditorLayout ? "Añadir nota…" : "Nota",
        value: this.userInputs[keyId]?.Note || "",
        onChange: (e) => this.handleInputChange(keyId, "Note", e.target.value),
        style: noteTextareaStyle,
      };
      const individualSizing = this.individualEditorLayout ? getIndividualEditorSizing(m) : null;
      return (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "stretch",
            width: "100%",
            ...(individualSizing ? centeredDataBlockStyle(individualSizing.noteWidth) : {}),
          }}
        >
          {this.individualEditorLayout ? (
            <AutoGrowingNoteTextarea {...textareaProps} />
          ) : (
            <textarea {...textareaProps} />
          )}
        </div>
      );
    }

    // Modo lectura (detalle)
    const txt = (m?.Note ?? m?.note ?? "").toString().trim();
    return (
      <div
        style={{
          width: "100%",
          minHeight: 80,
          whiteSpace: "pre-wrap",
          overflowWrap: "break-word",
          wordBreak: "break-word",
          lineHeight: 1.35,
        }}
        title={txt || "Sin nota"}
      >
        {txt ? <span>{txt}</span> : <span style={{ opacity: 0.6 }}>-</span>}
      </div>
    );
  }
  // NumericInput controlado - usa SIEMPRE ctx.ui.numericProps.
  // En recaudaciones se restringe a enteros, incluso para campos monetarios.
  NumericInput({
    id,
    name,
    label,
    keyId,
    field,
    disabled,
    money = false,
    prefix = null,
    inputStyleOverride = null,
    warning = false,
    warningText = "",
    fullWidthLine = false,
  }) {
    const value = this.userInputs[keyId]?.[field] ?? "";
    const isCollected = isVisitMachineCollected(
      { LocalMachineId: keyId },
      {
        wasCollectedByLM: this.wasCollectedByLM,
        keyId,
      }
    );

    const base = this.numericProps || {};
    let mergedProps = base;
    let inputMode = "numeric";
    if (money === true) {
      mergedProps = {
        ...base,
        prefix: base?.prefix ?? "$ ",
        allowNegative: base?.allowNegative ?? false,
      };
    } else if (prefix != null) {
      mergedProps = { ...base, prefix };
      inputMode = "numeric";
    }

    const mergedStyle = {
      ...(mergedProps?.style || {}),
      ...(this.individualEditorLayout ? { flex: "1 1 0", minWidth: 0 } : {}),
      ...(inputStyleOverride || {}),
    };

    const lineStyle = this.individualEditorLayout
      ? {
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
        }
      : fullWidthLine
        ? {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 10,
            width: "100%",
          }
        : this.rowLine;

    return (
      <div style={lineStyle}>
        <span
          style={
            this.individualEditorLayout
              ? {
                  flex: "0 1 auto",
                  maxWidth: "45%",
                  minWidth: 0,
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                  lineHeight: 1.2,
                }
              : undefined
          }
        >
          {label}
        </span>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            flex: this.individualEditorLayout ? "1 1 0" : undefined,
            minWidth: this.individualEditorLayout ? 0 : undefined,
            width: this.individualEditorLayout ? "auto" : undefined,
          }}
        >
          <NumericFormat
            id={id}
            name={name}
            aria-label={this.makeLabel({ Name: name }, label)}
            inputMode={inputMode}
            value={value}
            {...mergedProps}
            style={mergedStyle}
            onValueChange={({ value: v }) => this.handleInputChange(keyId, field, v)}
            disabled={disabled ?? !isCollected}
          />
          {warning && (
            <Tooltip title={warningText} placement="top" arrow>
              <span
                style={{
                  color: "#d32f2f",
                  fontSize: 16,
                  fontWeight: 800,
                  lineHeight: 1,
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                !
              </span>
            </Tooltip>
          )}
        </span>
      </div>
    );
  }

  // Cada estrategia concreta implementa:
  // buildCells(m) => {Machine, In, Out?, Total?, Note? }
}
